import { useState, useEffect, useCallback } from "react";
import { Order } from "@/types/supabase";
import { fetchWithAuth } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export const useOrder = (orderId: string | null) => {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getAccessToken } = useAuth();

  const fetchOrder = useCallback(async () => {
    if (!orderId) {
      setOrder(null);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const token = await getAccessToken();
      const response = await fetchWithAuth(`/order/orders/${orderId}`, token);
      if (response.error) {
        console.error("Error fetching order:", response.error);
        throw response.error;
      }

      setOrder(response);
    } catch (err) {
      console.error("Error in fetchOrder:", err);
      setError(err instanceof Error ? err.message : "Error al cargar la orden");
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  const getTokenWithErrorHandling = useCallback(async () => {
    try {
      const token = await getAccessToken();
      return token;
    } catch (error) {
      console.error("Error getting access token:", error);
      setError(
        "Error de autenticación. Por favor, intente iniciar sesión nuevamente.",
      );
      throw error;
    }
  }, [getAccessToken]);

  const fetchOrderCompletationTime = async (orderId: string) => {
    try {
      const token = await getTokenWithErrorHandling();

      const response = await fetchWithAuth(
        `/order/orders/${orderId}/duration`,
        token,
      );

      return response;
    } catch (err) {
      console.error("Error in fetchOrderCompletationTime:", err);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  return {
    order,
    loading,
    error,
    refetch: fetchOrder,
    fetchOrderCompletationTime,
  };
};
